import Decimal from "decimal.js";
import { appCurrency } from "./currency";

/**
 * Evaluates a mathematical expression string and returns the numeric result.
 * Supports +, -, *, /, parentheses, and negative numbers.
 * @param expr The mathematical expression to evaluate
 * @returns The evaluated result as a number
 * @throws Error if the expression is invalid
 */
export function evaluateExpression(expr: string): Decimal {
  let pos = 0;

  const parseNumber = (): Decimal => {
    let num = "";
    while (pos < expr.length && /[0-9., ]/.test(expr[pos])) {
      num += expr[pos++];
    }

    const curr = appCurrency(num);

    return new Decimal(curr.value);
  };

  const parseFactor = (): Decimal => {
    while (pos < expr.length && expr[pos] === " ") pos++;

    if (expr[pos] === "(") {
      pos++;
      const result = parseExpression();
      pos++; // skip ')'
      return result;
    }

    if (expr[pos] === "-") {
      pos++;
      return parseFactor().neg();
    }

    return parseNumber();
  };

  const parseTerm = (): Decimal => {
    let result = parseFactor();

    while (pos < expr.length) {
      while (pos < expr.length && expr[pos] === " ") pos++;

      if (expr[pos] === "*") {
        pos++;
        result = result.mul(parseFactor());
      } else if (expr[pos] === "/") {
        pos++;
        result = result.div(parseFactor());
      } else {
        break;
      }
    }

    return result;
  };

  const parseExpression = (): Decimal => {
    let result = parseTerm();

    while (pos < expr.length) {
      while (pos < expr.length && expr[pos] === " ") pos++;

      if (expr[pos] === "+") {
        pos++;
        result = result.add(parseTerm());
      } else if (expr[pos] === "-") {
        pos++;
        result = result.sub(parseTerm());
      } else {
        break;
      }
    }

    return result;
  };

  return parseExpression();
}
